import { useMemo, useState } from "react";
import { Checkbox, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { PaginationControls } from "../../../components/PaginationControls/index.js";
import type { ScanRow } from "../../../api/contactRoleEndDateBackfill.service.js";
import styles from "./ContactRoleEndDateBackfill.module.css";

/**
 * The scan-results table (SOFTSUPP-506). The scan returns ~15,600 rows, which
 * jsdom would survive but a browser tab would not enjoy — so the table is
 * paginated client-side and only the current page is rendered. Selection is a
 * Set of contactRoleId held by the PARENT, not per-page state, so ticking a
 * row on page 3 survives every page change and filter tweak.
 */

export const ROWS_PER_PAGE = 100;

type TableRow = {
  id: string;
  position: number;
  contactRoleId: string;
  contactName: string;
  companyName: string;
  officeName: string;
  roleType: string | null;
  startDate: string | null;
  updatedAt: string;
  editedAfterCutover: boolean;
  losesLastActiveRole: boolean;
  selected: boolean;
};

type ResultsTableProps = {
  /** The rows to show — already filtered by the parent. */
  rows: ScanRow[];
  selected: ReadonlySet<string>;
  onToggle: (contactRoleId: string) => void;
};

/** ISO timestamps read badly in a dense table; the date part is what matters. */
function datePart(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "—";
}

export function ResultsTable({ rows, selected, onToggle }: ResultsTableProps) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  // Clamped rather than reset: a filter that shrinks the list below the
  // current page must not strand the operator on an empty page.
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;

  const pageRows = useMemo<TableRow[]>(
    () =>
      rows.slice(startIndex, endIndex).map((row, index) => ({
        id: row.contactRoleId,
        position: startIndex + index + 1,
        contactRoleId: row.contactRoleId,
        contactName: row.contactName,
        companyName: row.companyName,
        officeName: row.officeName,
        roleType: row.roleType,
        startDate: row.startDate,
        updatedAt: row.updatedAt,
        editedAfterCutover: row.editedAfterCutover,
        losesLastActiveRole: row.losesLastActiveRole,
        selected: selected.has(row.contactRoleId),
      })),
    [rows, startIndex, endIndex, selected],
  );

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: "",
        accessor: "selected",
        render: (_value, row) => (
          <Checkbox
            aria-label={`Select role ${row.position} — ${row.contactName} (${row.companyName})`}
            checked={row.selected}
            onChange={() => onToggle(row.contactRoleId)}
          />
        ),
      },
      {
        header: "Contact",
        accessor: "contactName",
        wrap: true,
        render: (_value, row) => (
          <span className={styles.name} title={row.contactRoleId}>
            {row.contactName || "—"}
          </span>
        ),
      },
      {
        header: "Company",
        accessor: "companyName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.companyName || "—"}</span>,
      },
      {
        header: "Office",
        accessor: "officeName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.officeName || "—"}</span>,
      },
      {
        header: "Role type",
        accessor: "roleType",
        render: (_value, row) => <span className={styles.mono}>{row.roleType ?? "—"}</span>,
      },
      {
        header: "Start date",
        accessor: "startDate",
        render: (_value, row) => (
          <span className={styles.mono} title={row.startDate ?? undefined}>
            {datePart(row.startDate)}
          </span>
        ),
      },
      {
        header: "Updated at",
        accessor: "updatedAt",
        render: (_value, row) => (
          <span className={styles.mono} title={row.updatedAt}>
            {datePart(row.updatedAt)}
          </span>
        ),
      },
      {
        header: "Flags",
        accessor: "editedAfterCutover",
        render: (_value, row) =>
          row.editedAfterCutover || row.losesLastActiveRole ? (
            <span className={styles.flags}>
              {row.editedAfterCutover ? (
                <span className={`${styles.pill} ${styles.pillWarning}`}>edited after cutover</span>
              ) : null}
              {row.losesLastActiveRole ? (
                <span className={`${styles.pill} ${styles.pillError}`}>loses last active role</span>
              ) : null}
            </span>
          ) : (
            <span className={styles.mono} aria-hidden="true">
              —
            </span>
          ),
      },
    ],
    [onToggle],
  );

  return (
    <div className={styles.stack}>
      <DataTable
        data={pageRows}
        columns={columns}
        emptyMessage="No contact roles match."
        rowClassName={row => (row.selected ? "" : (styles.rowDeselected as string))}
      />
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        startIndex={startIndex}
        endIndex={endIndex}
        totalCount={rows.length}
        itemName="roles"
        onPageChange={setPage}
      />
    </div>
  );
}
